"use client";
import { use, useEffect, useRef, useCallback } from "react";
import { useParams, usePathname } from "next/navigation";
import DynamicBreadcrumb from "@/app/components/elements/breadcrumb/DynamicBreadcrumb";
import ProductsLayout from "@/app/components/common/products/ProductsLayout";
import ProductsHeader from "@/app/components/common/products/ProductsHeader";
import GenericProductsPageSkeleton from "@/app/components/elements/skeletons/GenericProductsPageSkeleton";
import useSimilarProducts from "@/app/lib/features/similarProducts/similarProducts.hook";
import useFilters from "@/app/lib/features/filters/filters.hook";
import useFilterUrlSync from "@/app/hooks/useFilterUrlSync";
import useDebounce from "@/app/hooks/useDebounce";
import {
  DEFAULT_FILTER_REQUEST,
  FilterRequest,
} from "@/app/lib/features/filters/filters.types";
import { SimilarProductsData } from "@/app/lib/features/similarProducts/similarProducts.types";

const BASE_FILTER_REQUEST: FilterRequest = {
  section_id: DEFAULT_FILTER_REQUEST.section_id,
  seque: DEFAULT_FILTER_REQUEST.seque,
};

interface BenzerUrunlerClientProps {
  slug?: string;
  initialDataPromise: Promise<SimilarProductsData | null>;
}

export default function BenzerUrunlerClient({
  slug: initialSlug,
  initialDataPromise,
}: BenzerUrunlerClientProps) {
  // Streaming: promise pending iken suspend → page.tsx'teki Suspense fallback'i
  // (GenericProductsPageSkeleton) görünür; boş ekran olmaz.
  const initialData = use(initialDataPromise);
  const params = useParams();
  const slug = (params.slug as string) || initialSlug || "";

  const {
    selected,
    updateSelected,
    fetchFilters,
    fetchGeneralFilters,
    reset: resetFilters,
    clearFilters,
    userInteractionCount,
  } = useFilters();

  const pathname = usePathname();
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setTimeout(() => {
      window.history.replaceState(null, "", pathname);
    }, 0);
  }, [clearFilters, pathname]);

  const {
    products,
    pagination,
    title,
    hasMore,
    isLoading,
    isSuccess,
    currentSlug,
    ssrHydrated,
    fetchSimilarProducts,
    loadMore,
    seedSimilarProducts,
  } = useSimilarProducts();

  const { activeFilterRequest, initReady } = useFilterUrlSync({
    selected,
    updateSelected,
    baseFilterRequest: BASE_FILTER_REQUEST,
    interactionCount: userInteractionCount,
  });

  const debouncedKey = useDebounce(JSON.stringify(activeFilterRequest), 500);
  const lastFetchedKeyRef = useRef<string | null>(null);
  const initialFetchSkippedRef = useRef(false);

  // SSR seed — mount'ta Redux boşsa ilk sayfayı yükle (GenericProductsPage pattern).
  // loadInitialSimilarProducts ssrHydrated=true yapar; fetch effect ilk client
  // fetch'ini bu bayrağa bakarak atlar — pending page-1 null flash'ı olmaz.
  const ssrSeededRef = useRef(false);
  useEffect(() => {
    if (ssrSeededRef.current) return;
    if (initialData && slug) {
      ssrSeededRef.current = true;
      seedSimilarProducts(initialData, slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // productList effect'ten ÖNCE tanımlanmalı — hook kuralları gereği
  const initialItems = initialData?.items ?? [];
  const productList =
    isSuccess || (products?.length ?? 0) > 0 ? products ?? [] : initialItems;

  useEffect(() => {
    if (!initReady) return;
    if (!slug) return;
    fetchFilters(activeFilterRequest);
    fetchGeneralFilters({});

    // SSR ile hydrate edildiyse ilk client fetch'ini atla — cache'ten gelen
    // veriyi olduğu gibi göster (skeleton/flash yok). Sonraki filtre değişimlerinde
    // normal fetch devreye girer.
    if (
      ssrHydrated &&
      currentSlug === slug &&
      !initialFetchSkippedRef.current
    ) {
      initialFetchSkippedRef.current = true;
      lastFetchedKeyRef.current = `${slug}::${debouncedKey}`;
      return;
    }
    initialFetchSkippedRef.current = true;

    // Back-nav: aynı slug + aynı filtre key + ürünler mevcutsa fetch atla
    const cacheKey = `${slug}::${debouncedKey}`;
    if (lastFetchedKeyRef.current === cacheKey && productList.length > 0) return;
    lastFetchedKeyRef.current = cacheKey;
    fetchSimilarProducts(slug, {
      ...activeFilterRequest,
      page: 1,
      per_page: 20,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey, slug, initReady]);

  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [resetFilters]);
  const pageTitle = title || initialData?.title || "Benzer Ürünler";
  const hasResults = isSuccess || initialItems.length > 0;
  const totalProducts = pagination?.total ?? initialData?.pagination?.total;

  if (isLoading && productList.length === 0 && initialItems.length === 0) {
    return <GenericProductsPageSkeleton />;
  }

  return (
    <div className="container-main">
      <DynamicBreadcrumb title={pageTitle} />
      <ProductsLayout
        header={(onOpenFilter) => (
          <ProductsHeader
            title={pageTitle}
            productCount={hasResults && totalProducts ? totalProducts : undefined}
            onOpenFilter={onOpenFilter}
          />
        )}
        products={productList}
        hasMore={hasMore}
        isLoading={isLoading}
        isSuccess={hasResults}
        onLoadMore={loadMore}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
}